import { useState } from 'react';
import { EMPLOYEE_STATUSES, EMPLOYMENT_TYPES, type EmployeeDTO, type EmployeeInput } from '@em/shared';
import { api, queryClient, useSites, useTeams, useUsers } from '../../api';
import { useAuth } from '../../auth/AuthContext';
import { titleCase, todayIn } from '../../components/format';
import { Modal } from '../../components/Modal';
import { useToast } from '../../components/Toast';
import { Button, Checkbox, ErrorAlert, Field, Input, Select } from '../../components/ui';

const EMPLOYMENT_LABELS = { full_time: 'Full time', part_time: 'Part time', casual: 'Casual' } as const;

function toInput(e?: EmployeeDTO): EmployeeInput {
  return {
    employeeNumber: e?.employeeNumber ?? '',
    firstName: e?.firstName ?? '',
    lastName: e?.lastName ?? '',
    preferredName: e?.preferredName ?? null,
    email: e?.email ?? null,
    mobile: e?.mobile ?? null,
    employmentType: e?.employmentType ?? 'full_time',
    startDate: e?.startDate ?? todayIn('Australia/Sydney'),
    endDate: e?.endDate ?? null,
    teamId: e?.teamId ?? null,
    primarySiteId: e?.primarySiteId ?? null,
    supervisorUserId: e?.supervisorUserId ?? null,
    status: e?.status ?? 'active',
    myobUid: e?.myobUid ?? null,
    myobCardId: e?.myobCardId ?? null,
    kioskEnabled: e?.kioskEnabled ?? true,
  };
}

/** Create or edit an employee profile (payroll admin and owner). */
export function EmployeeForm({ employee, onClose, onSaved }: { employee?: EmployeeDTO; onClose: () => void; onSaved?: (e: EmployeeDTO) => void }) {
  const { can } = useAuth();
  const teams = useTeams();
  const sites = useSites();
  const users = useUsers(can('owner'));
  const toast = useToast();
  const [f, setF] = useState<EmployeeInput>(() => toInput(employee));
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof EmployeeInput>(k: K, v: EmployeeInput[K]) => setF((x) => ({ ...x, [k]: v }));
  const blank = (v: string) => (v.trim() === '' ? null : v.trim());

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const saved = employee ? await api.updateEmployee(employee.id, f) : await api.createEmployee(f);
      await queryClient.invalidateQueries({ queryKey: ['employees'] });
      await queryClient.invalidateQueries({ queryKey: ['employee', saved.id] });
      const pinNote =
        saved.defaultPin === 'set'
          ? 'Their PIN is the last 4 digits of their mobile. They use it for the kiosk and to sign in to the phone app with their mobile number.'
          : saved.defaultPin === 'taken'
            ? 'The last 4 digits of their mobile are already someone else’s PIN, so set a PIN for them.'
            : !employee && !saved.hasPin
              ? 'Add their mobile number (or set a PIN) so they can use the kiosk and the phone app.'
              : undefined;
      toast.success(employee ? 'Employee saved' : 'Employee added', [!employee ? 'Assign pay rules next so their hours can be calculated.' : null, pinNote].filter(Boolean).join(' ') || undefined);
      onSaved?.(saved);
      onClose();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={employee ? `Edit ${employee.displayName}` : 'Add employee'}
      wide
      onClose={onClose}
      onSubmit={save}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" loading={busy}>
            {employee ? 'Save' : 'Add employee'}
          </Button>
        </>
      }
    >
      <div className="stack">
        <ErrorAlert error={error} />
        <div className="form-grid three">
          <Field label="First name" required>
            <Input value={f.firstName} onChange={(e) => set('firstName', e.target.value)} required />
          </Field>
          <Field label="Last name" required>
            <Input value={f.lastName} onChange={(e) => set('lastName', e.target.value)} required />
          </Field>
          <Field label="Preferred name">
            <Input value={f.preferredName ?? ''} onChange={(e) => set('preferredName', blank(e.target.value))} />
          </Field>
          <Field label="Employee number" required>
            <Input value={f.employeeNumber} onChange={(e) => set('employeeNumber', e.target.value)} required maxLength={32} />
          </Field>
          <Field label="Email">
            <Input type="email" value={f.email ?? ''} onChange={(e) => set('email', blank(e.target.value))} />
          </Field>
          <Field label="Mobile">
            <Input type="tel" value={f.mobile ?? ''} onChange={(e) => set('mobile', blank(e.target.value))} />
          </Field>
          <Field label="Employment type" required>
            <Select value={f.employmentType} onChange={(e) => set('employmentType', e.target.value as EmployeeInput['employmentType'])}>
              {EMPLOYMENT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {EMPLOYMENT_LABELS[t]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Start date" required>
            <Input type="date" value={f.startDate} onChange={(e) => set('startDate', e.target.value)} required />
          </Field>
          <Field label="End date" hint="Leave empty while employed">
            <Input type="date" value={f.endDate ?? ''} onChange={(e) => set('endDate', e.target.value || null)} />
          </Field>
          <Field label="Status" hint={f.status !== 'active' ? 'Suspending or terminating revokes their phone straight away' : undefined}>
            <Select value={f.status} onChange={(e) => set('status', e.target.value as EmployeeInput['status'])}>
              {EMPLOYEE_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {titleCase(s)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Team">
            <Select value={f.teamId ?? ''} onChange={(e) => set('teamId', e.target.value || null)}>
              <option value="">No team</option>
              {teams.data?.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Primary site">
            <Select value={f.primarySiteId ?? ''} onChange={(e) => set('primarySiteId', e.target.value || null)}>
              <option value="">No primary site</option>
              {sites.data?.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Direct supervisor" hint={!can('owner') ? 'Only the owner can list portal users; team supervisors still apply.' : undefined}>
            <Select value={f.supervisorUserId ?? ''} onChange={(e) => set('supervisorUserId', e.target.value || null)} disabled={!can('owner')}>
              <option value="">{f.supervisorUserId && !users.data ? 'Set (hidden)' : 'None (use team supervisors)'}</option>
              {users.data
                ?.filter((u) => u.status === 'active')
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
            </Select>
          </Field>
          <Field label="MYOB card ID" hint="Used to match the import file">
            <Input value={f.myobCardId ?? ''} onChange={(e) => set('myobCardId', blank(e.target.value))} />
          </Field>
          <Field label="MYOB employee UID" hint="Set by auto-match when MYOB is connected">
            <Input className="mono" value={f.myobUid ?? ''} onChange={(e) => set('myobUid', blank(e.target.value))} />
          </Field>
          <div className="field" style={{ justifyContent: 'flex-end', paddingBottom: 8 }}>
            <Checkbox label="Can use the factory kiosk" checked={!!f.kioskEnabled} onChange={(v) => set('kioskEnabled', v)} />
          </div>
        </div>
      </div>
    </Modal>
  );
}
